// Avisa que saiu versão nova, sem recarregar por conta própria.
//
// O problema que isto resolve: quem está com o app ABERTO na hora do deploy
// continua rodando o código antigo até fechar e abrir. Se o deploy mexeu também
// na API, essa pessoa toma erro sem entender por quê — e ninguém no balcão vai
// adivinhar que precisa fechar o app.
//
// Como sabe que envelheceu: comparando o próprio `index.html`. O CRA gera os
// bundles com hash no nome (`main.9f3a2b.js`), então o nome muda a cada build.
// Basta ler o index.html do servidor (que o nginx serve sem cache) e comparar
// com os scripts que ESTA página carregou. Não precisa de arquivo de versão, de
// build step, nem de endpoint na API — o hash do bundle já é a versão.
//
// Em desenvolvimento nada acontece: ali o bundle se chama `bundle.js`, sem hash,
// e a comparação nunca acusa diferença.
import React, { useCallback, useEffect, useRef, useState } from 'react';

const DE_QUANTO_EM_QUANTO = 10 * 60 * 1000; // 10 min
// Quem dispensou não é incomodado de novo tão cedo — mas volta a ser avisado,
// senão a pessoa fica na versão velha o dia inteiro por causa de um toque.
const SILENCIO_APOS_DISPENSAR = 2 * 60 * 60 * 1000; // 2h

const VERDE = '#3D8B37';

// Os bundles que ESTA página está rodando, na ordem em que aparecem.
function bundlesDaPagina() {
  return [...document.querySelectorAll('script[src]')]
    .map((el) => el.getAttribute('src') || '')
    .filter((src) => src.includes('/static/js/'))
    .join(',');
}

// Os bundles que o servidor está entregando AGORA.
async function bundlesDoServidor() {
  // `no-store` + carimbo na URL: o index.html não pode vir de cache nenhum,
  // senão a comparação seria com a própria versão antiga.
  const r = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
  if (!r.ok) throw new Error('index.html indisponível');
  const html = await r.text();
  return (html.match(/\/static\/js\/[^"']+\.js/g) || []).join(',');
}

export default function AvisoNovaVersao() {
  const [temNova, setTemNova] = useState(false);
  const meusBundles = useRef(bundlesDaPagina());
  const dispensadoEm = useRef(0);

  const conferir = useCallback(async () => {
    if (!meusBundles.current) return; // dev, ou página sem bundle com hash
    if (dispensadoEm.current && Date.now() - dispensadoEm.current < SILENCIO_APOS_DISPENSAR) return;
    try {
      const doServidor = await bundlesDoServidor();
      if (doServidor && doServidor !== meusBundles.current) setTemNova(true);
    } catch {
      // Sem rede ou servidor reiniciando: não é assunto pra mostrar pro usuário.
    }
  }, []);

  useEffect(() => {
    conferir();
    const t = setInterval(conferir, DE_QUANTO_EM_QUANTO);
    // O tablet fica com o app aberto o dia todo. Voltar pra ele é o melhor
    // momento pra conferir — e é quando atualizar custa menos.
    const aoVoltar = () => { if (document.visibilityState === 'visible') conferir(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', aoVoltar); };
  }, [conferir]);

  if (!temNova) return null;

  const dispensar = () => { dispensadoEm.current = Date.now(); setTemNova(false); };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20050,
        background: VERDE, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        padding: 'calc(8px + env(safe-area-inset-top, 0px)) 14px 8px',
        fontSize: '0.85rem',
      }}
    >
      <span>Tem uma versão nova do Fio.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          background: '#fff', color: VERDE, border: 'none', borderRadius: 7,
          padding: '6px 14px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        Atualizar
      </button>
      {/* Dispensar existe porque atualizar RECARREGA: quem está no meio de uma
          mensagem escolhe a hora. Não recarregamos sozinhos por isso mesmo. */}
      <button
        type="button"
        onClick={dispensar}
        title="Agora não"
        aria-label="Agora não"
        style={{
          background: 'transparent', color: '#fff', border: 'none',
          padding: '2px 4px', fontSize: '1rem', lineHeight: 1, cursor: 'pointer', opacity: 0.85,
        }}
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
}
